import { Button, Grid, InputLabel, OutlinedInput, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Typography, Divider } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DefaultDrawer from '../../../../components/default-drawer';
import { IPurchaseItemResultResponse } from '../../../../model/scp/purchase-item-result/response/purchase-item-result-response';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import InputMask from 'react-input-mask';
import { PurchaseItemResultProvider, usePurchaseItemResultState } from './context/purchase-item-result-context';
import { usePurchaseItemResultController } from './context/purchase-item-result-controller';
import { IUpdatePurchaseItemResultRequest } from '../../../../model/scp/purchase-item-result/request/update-purchase-item-result-request';

export interface IPurchaseItemResultDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    purchaseId: number;
    itemNumber: number;
    resultData?: IPurchaseItemResultResponse | null;
    onSave: () => void;
}

function PurchaseItemResultDrawerContent({ onSave, onClose }: { onSave: () => void; onClose: () => void }) {
    const state = usePurchaseItemResultState();
    const controller = usePurchaseItemResultController(onSave, onClose);

    const {
        resultRequest,
        isEditMode,
        legalNatures,
        isOpen
    } = state;

    const {
        handleOnChange,
        handleCurrencyChange,
        handleSave
    } = controller;

    return (
        <DefaultDrawer
            title={isEditMode ? "Editar Resultado do Item" : "Adicionar Resultado do Item"}
            isOpen={isOpen}
            onClose={onClose}
            actions={() => (
                <Button variant="contained" onClick={handleSave} endIcon={<SaveIcon />}>
                    Salvar
                </Button>
            )}
        >
            <Grid container spacing={3}>
                {/* Seção: Dados do Fornecedor */}
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                        Dados do Fornecedor
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Tipo de Pessoa *</InputLabel>
                    <Select
                        autoComplete='off'
                        name="personTypeId"
                        value={resultRequest.personTypeId}
                        onChange={handleOnChange}
                        fullWidth
                    >
                        <MenuItem value={'PJ'}>1 - Pessoa Jurídica</MenuItem>
                        <MenuItem value={'PF'}>2 - Pessoa Física</MenuItem>
                        <MenuItem value={'PE'}>3 - Pessoa Estrangeira</MenuItem>
                    </Select>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>
                        {resultRequest.personTypeId == '1'
                            ? 'CPF do Fornecedor'
                            : resultRequest.personTypeId == '2'
                                ? 'Identificação do Fornecedor Estrangeiro'
                                : 'CNPJ do Fornecedor'}
                    </InputLabel>
                    <InputMask
                        mask={resultRequest.personTypeId == '1'
                            ? '999.999.999-99'
                            : resultRequest.personTypeId == '2'
                                ? ''
                                : '99.999.999/9999-99'}
                        name="supplierNi"
                        id="supplierNi"
                        autoComplete='off'
                        value={resultRequest.supplierNi || ''}
                        onChange={handleOnChange}
                        onBlur={handleOnChange}
                    >
                        <OutlinedInput
                            fullWidth
                            error={!resultRequest.supplierNi}
                            name="supplierNi"
                            id="supplierNi"
                            value={resultRequest.supplierNi || ''}
                            onChange={handleOnChange}
                            onBlur={handleOnChange}
                        />
                    </InputMask>
                </Grid>

                <Grid item xs={12} sm={12}>
                    <InputLabel>Razão Social do Fornecedor *</InputLabel>
                    <OutlinedInput
                        autoComplete='off'
                        name="supplierCompanyName"
                        value={resultRequest.supplierCompanyName || ''}
                        onChange={handleOnChange}
                        fullWidth
                        error={!resultRequest.supplierCompanyName}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Porte do Fornecedor *</InputLabel>
                    <Select
                        autoComplete='off'
                        name="supplierSizeId"
                        value={resultRequest.supplierSizeId || ''}
                        onChange={handleOnChange}
                        fullWidth
                        error={!resultRequest.supplierSizeId}
                    >
                        <MenuItem value="">
                            <em>Selecionar</em>
                        </MenuItem>
                        <MenuItem value={1}>1 - ME</MenuItem>
                        <MenuItem value={2}>2 - EPP</MenuItem>
                        <MenuItem value={3}>3 - Demais</MenuItem>
                        <MenuItem value={4}>4 - Nao se Aplica</MenuItem>
                        <MenuItem value={5}>5 - Nao Informado</MenuItem>
                    </Select>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Natureza Jurídica</InputLabel>
                    <Select
                        autoComplete='off'
                        name="legalNatureId"
                        value={resultRequest.legalNatureId || ''}
                        onChange={handleOnChange}
                        fullWidth
                    >
                        <MenuItem value="">
                            <em>Selecionar</em>
                        </MenuItem>
                        {legalNatures.map((nature) => (
                            <MenuItem key={nature.id} value={nature.id}>
                                {nature.id} - {nature.name}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Descrição da Marca</InputLabel>
                    <OutlinedInput
                        autoComplete='off'
                        name="descriptionBrand"
                        value={resultRequest.descriptionBrand || ''}
                        onChange={handleOnChange}
                        fullWidth
                    />
                </Grid>

                {/* Seção: Valores do Resultado */}
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                        Valores do Resultado
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel>Quantidade Homologada *</InputLabel>
                    <BrazilianNumberInput
                        value={resultRequest.approvedQuantity}
                        onChange={(value) => handleCurrencyChange(value.toString(), 'approvedQuantity')}
                        decimals={4}
                        placeholder="0,0000"
                        error={resultRequest.approvedQuantity <= 0}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel>Preço Unitário Homologado *</InputLabel>
                    <BrazilianNumberInput
                        value={resultRequest.approvedUnitPrice}
                        onChange={(value) => handleCurrencyChange(value.toString(), 'approvedUnitPrice')}
                        decimals={4}
                        prefix="R$"
                        placeholder="0,0000"
                        error={resultRequest.approvedUnitPrice <= 0}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel>Preço Total Homologado</InputLabel>
                    <BrazilianNumberInput
                        value={resultRequest.approvedTotalPrice}
                        onChange={() => {}}
                        decimals={4}
                        prefix="R$"
                        placeholder="0,0000"
                        disabled
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <InputLabel>Percentual de Desconto</InputLabel>
                    <BrazilianNumberInput
                        value={resultRequest.discountPercentage}
                        onChange={(value) => handleCurrencyChange(value.toString(), 'discountPercentage')}
                        decimals={2}
                        placeholder="0,00"
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Data do Resultado</InputLabel>
                    <OutlinedInput
                        autoComplete='off'
                        name="resultDate"
                        type="date"
                        value={resultRequest.resultDate ? new Date(resultRequest.resultDate).toISOString().split('T')[0] : ''}
                        onChange={handleOnChange}
                        fullWidth
                    />
                </Grid>

                {isEditMode && (
                    <>
                        {/* Seção: Alteração de Resultado (Modo Edição) */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                Alteração de Resultado
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                            <InputLabel>Situação do Resultado do Item</InputLabel>
                            <Select
                                autoComplete='off'
                                name="situationId"
                                value={(resultRequest as IUpdatePurchaseItemResultRequest).situationId || ''}
                                onChange={handleOnChange}
                                fullWidth
                            >
                                <MenuItem value="">
                                    <em>Selecionar</em>
                                </MenuItem>
                                <MenuItem value={1}>1 - Informado</MenuItem>
                                <MenuItem value={2}>2 - Cancelado</MenuItem>
                            </Select>
                        </Grid>

                        <Grid item xs={12}>
                            <InputLabel>Justificativa</InputLabel>
                            <OutlinedInput
                                autoComplete='off'
                                name="justification"
                                value={(resultRequest as IUpdatePurchaseItemResultRequest).justification || ''}
                                onChange={handleOnChange}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </>
                )}

                {/* Seção: Indicadores */}
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                        Indicadores
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="subcontractingIndicator"
                                    checked={resultRequest.subcontractingIndicator}
                                    onChange={handleOnChange}
                                />
                            }
                            label="Indicador de Subcontratação"
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={12}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="meEppBenefitApplication"
                                    checked={resultRequest.meEppBenefitApplication}
                                    onChange={handleOnChange}
                                />
                            }
                            label="Indicador de Aplicação do Benefício de ME/EPP"
                        />
                    </FormGroup>
                </Grid>
            </Grid>
        </DefaultDrawer>
    );
}

export default function PurchaseItemResultDrawer({ props }: { props: IPurchaseItemResultDrawerProps }) {
    return (
        <PurchaseItemResultProvider
            initialIsOpen={props.isOpen}
            initialPurchaseId={props.purchaseId}
            initialItemNumber={props.itemNumber}
            initialResultData={props.resultData}
        >
            <PurchaseItemResultDrawerContent onSave={props.onSave} onClose={props.onClose} />
        </PurchaseItemResultProvider>
    );
}
